import { ReactComponent as IconPrevious } from '@/assets/svgs/previous-arrow.svg'
import AdditionalZone from '@/components/AdditionalZone'
import Breadcrumbs from '@/components/Breadcrumbs'
import Button from '@/components/Button'
import Input from '@/components/Input'
import Portal from '@/components/Portal'
import SelectorBox2 from '@/components/SelectorBox2'
import Toast2 from '@/components/Toast2'
import useBehalfPanel from '@/hooks/use-behalf-panel'
import useCDI from '@/hooks/use-cdi'
import useComputed from '@/hooks/use-computed'
import useConfig from '@/hooks/use-config'
import usePage from '@/hooks/use-page'
import "@/translations/i18n"
import handle from '@/utils/handle.js'
import rem from '@/utils/rem'
import classname from 'classnames'
import produce from 'immer'
import { isEmpty, orderBy } from 'lodash'
import { Fragment, useContext, useEffect, useRef, useState } from 'react'
import DatePicker from "react-datepicker"
import ReactDOM from 'react-dom'
import { useTranslation } from "react-i18next"
import KeyboardEventHandler from '@infinium/react-keyboard-event-handler'
import { useNavigate, useLocation } from '@/lib/router-dom'
import { ReactComponent as IconStepper } from '@/assets/svgs/stepper.svg'
import { ToastContext } from '@/context/toast/index'
import Ajv from "ajv";
import addFormats from "@/utils/custom-ajv-formats";
import Stepper from '@/componentsDS/Stepper/Stepper'

const ajv = new Ajv({ allErrors: true });
addFormats(ajv);

const Page = () => {

	/*=================================================
		General
	/================================================*/

	const { t } = useTranslation();
	const pageTitle = t("requestData");
	const addToastMessage = useContext(ToastContext)

	const { breadcrumbsItems, setBreadcrumbsItems } = usePage({ title: pageTitle, breadcrumbs: [t("cdiPitstop"), t("receivedData"), pageTitle, t("chooseDataElement")] })
	const { goTo } = useNavigate();
	/*=================================================
		Error Modal
	/================================================*/
	const [error, setError] = useState()
	function handleError() {
		setError()
	}

	/*=================================================
		API
	/================================================*/

	const { consumeItems, error: erroConfig } = useConfig()
	const { pull } = useCDI()
	const [selectedDataElement, setSelectedDataElement, [participants, querySchema]] = useComputed({},
		value => value && value.from ? orderBy(value.from, [participant => participant.name.toLowerCase()], ['asc']) : [],
		value => value.querySchema
	)
	const [selectedParticipants, setSelectedParticipants] = useState([])
	const [selectedOnBehalf, setSelectedOnBehalf] = useState(null)
	const [onBehalfOf, setOnBehalfOf] = useState([])

	useEffect(() => {
		setSelectedParticipants([])
	}, [selectedDataElement])

	useEffect(() => {
		setOnBehalfOf(selectedParticipants.reduce((pre, cur) => [...pre, ...cur.on_behalf_of], []))
	}, [selectedParticipants])

	const ElementsList = () => {

		if (!consumeItems) {
			return ''
		}

		const sortedConsumeItems = orderBy(consumeItems.filter(item => !isEmpty(item.querySchema)), [item => item.name.toLowerCase()], ['asc'])

		const list = sortedConsumeItems.map((element, index) => {

			const active = selectedDataElement.id === element.id

			return (
				<div className="data-element-selectors__col" key={index} onClick={() => setSelectedDataElement(element)}>
					<SelectorBox2 label={element.name} active={active} />
				</div>
			)
		})

		return list.length > 0 ? list : <div className="data-element-selectors__col">There are no data elements to consume</div>
	}

	const ParticipantsList = () => {

		if (!participants) {
			return ''
		}

		const list = participants.filter(item => (!("for_principals" in item)) && (!("to_principals" in item))).map((element, index) => {

			const active = selectedParticipants.find(participant => participant.id === element.id)
			const activeIndex = selectedParticipants.findIndex(participant => participant.id === element.id)

			function toggleParticipant() {

				if (active) {
					const updatedSelectedParticipants = produce(selectedParticipants, draft => {
						draft.splice(activeIndex, 1)
					})
					return setSelectedParticipants(updatedSelectedParticipants)
				}

				const updatedSelectedParticipants = produce(selectedParticipants, draft => {
					draft.splice(activeIndex, 1)
					draft.push(element)
				})
				return setSelectedParticipants(updatedSelectedParticipants)
			}

			return (
				<div className="data-element-selectors__col" key={index} onClick={() => toggleParticipant()}>
					<SelectorBox2 label={element.name} image={element.iconUrl} active={active} />
				</div>
			)
		})

		return list.length > 0 ? list : <div className="data-element-selectors__col">There are no participants</div>

	}

	useEffect(() => {
		setError(erroConfig)
	}, [erroConfig])

	/*=================================================
		Steps
	/================================================*/
	const stepArr =[
		{stepTitle:t('chooseDataElement') },
		{stepTitle:t('chooseParticipant') },
		{stepTitle:t('dataInformation') }
	]
	const steps = [...stepArr];
	const [selectedSteps] = useState([t("selectedDataElements"), t("selectedParticipant"), t("dataInformation")])
	const [step, setStep] = useState(1)

	const [stepOneStatus, setStepOneStatus] = useState(false)
	const [stepTwoStatus, setStepTwoStatus] = useState(false)
	const [stepThreeBStatus, setStepThreeBStatus] = useState(true)

	useEffect(() => {

		if (selectedDataElement && selectedDataElement.id) {
			return setStepOneStatus(true)
		}

		setStepOneStatus(false)
	}, [selectedDataElement])

	useEffect(() => {

		if (selectedParticipants.length > 0) {
			return setStepTwoStatus(true)
		}

		setStepTwoStatus(false)
	}, [selectedParticipants])

	useEffect(() => {

		const updatedBreadcrumbs = produce(breadcrumbsItems, draft => {
			draft[draft.length - 1] = steps[step - 1].stepTitle
		})
		return setBreadcrumbsItems(updatedBreadcrumbs)

	}, [step])

	const [ behalfPanel ] = useBehalfPanel(onBehalfOf)

	const dataElementSelectorStyles = {
		// marginTop: rem(20)
	}


	const SelectedListDetails = () => {

		const list = selectedSteps.map((item, index) => {

			const stepClass = classname({
				'selector-box-selected-lists': true
			})


			return (
				index === 0 ?
				(selectedDataElement.id ?
				<div className={stepClass} key={index}>
					<div className='heading-light-color'>{item}</div>
					<div className='text-light-color'>{selectedDataElement.name}</div>
				</div>
				:
				<div className={stepClass} key={index}>
					<div className='heading-light-color'>{item}</div>
					<div className="light-color">None</div>
				</div>) : index === 1 ?
				(selectedParticipants[0]?.name ?
				<div className={stepClass} key={index}>
					<div className='heading-light-color'>{item}</div>
					<div className='text-light-color'>{selectedParticipants[0]?.name}</div>
				</div>
				:
				<div className={stepClass} key={index}>
					<div className='heading-light-color'>{item}</div>
					<div className="light-color">None</div>
				</div>) : ''
			)
		})
		return <div className="selectedLists">{list}</div>
	}

	function goToStep(step) {
		if (step < 1 || step > steps.length) {
			return
		}
		setStep(step)
	}

	function prevStep() {
		goToStep(step - 1)
	}

	function nextStep() {
		goToStep(step + 1)
	}

	function goBack() {
		goTo('/received-data')
	}

	/*=================================================
		Queries
	/================================================*/

	const [errors, setErrors] = useState([])
	const queryForm = useRef(null)
	const [queries, setQueries] = useState()
	const [startDate, setStartDate] = useState();
	const [currentDate, setCurrentDate] = useState();
	// useEffect(() => {
	// 	return setCurrentDate(null);
	// }, [startDate]);

	const parseQuery = (key, value, required = []) => {
		if (value.required) required = value.required
		if (value.type === 'object') {
			return Object.entries(value.properties).reduce((r, e) => [...r, ...parseQuery(e[0], e[1], required)], [])
		} else {
			return [
				{
					label: key.split('_').reduce((r, item) => `${r} ${item.slice(0, 1).toUpperCase()}${item.slice(1)}`, ''),
					type: value.type === 'number' ? 'number' : value.type === 'array' ? 'array' : !value.format ? 'string' : value.format,
					field: `${key}|${Math.random(1000)}`,
					description: value.description,
					required: required.includes(key)
				}
			]
		}
	}

	const getQuery = () => {
		let query
		if (querySchema.oneOf) {
			query = querySchema.oneOf.map(e => parseQuery(null, e))
		} else {
			query = [parseQuery(null, querySchema)]
		}
		setQueries(query)
	}

	useEffect(() => {
		if (!querySchema) {
			return
		}
		getQuery()
	}, [selectedDataElement])

	function onSelectedOnBehalf(params) {
		if (behalfPanel && params && params.id && params.id !== 'no') {
			setSelectedOnBehalf(params)
			setStepThreeBStatus(true)
		} else if (!behalfPanel) {
			setStepThreeBStatus(true)
		} else {
			setStepThreeBStatus(false)
		}
	}

	function onKeyDown(event) {
		if (event.key === 'Enter') {
			event.preventDefault()
			submit()
		}
	}

	const [submitLoading, setSubmitLoading] = useState()

	function convertStringtoIsoString(label) {
		// const array = text.split(" ");
		// const mergeDate = array[0] + "T" + array[1] + "." + array[2] + "Z"
		// const convertDate = new Date(mergeDate)
		// return convertDate.toISOString();
		// const localOffset = startDate.getTimezoneOffset() * 60000;
		if (label === ' From') {
			if (startDate && startDate !== '') {
				let tzoffset = (new Date(startDate)).getTimezoneOffset() * 60000;
				let localISOTime = (new Date(startDate - tzoffset)).toISOString().slice(0, -1);
				return localISOTime + "Z";
			}
			else {
				return undefined;
			}
		}
		else if (label === ' To') {
			if (currentDate && currentDate !== '') {
				let tzoffset = (new Date(currentDate)).getTimezoneOffset() * 60000;
				let localISOTime = (new Date(currentDate - tzoffset)).toISOString().slice(0, -1);
				return localISOTime + "Z";
			}
			else {
				return undefined;
			}
		}
		else {
			if (currentDate && currentDate !== '') {
				let tzoffset = (new Date(currentDate)).getTimezoneOffset() * 60000;
				let localISOTime = (new Date(currentDate - tzoffset)).toISOString().slice(0, -1);
				return localISOTime + "Z";
			}
			else {
				return undefined;
			}
		}
	}

	function convertStringtoDate(label) {
		if (label === ' From') {
			if (startDate && startDate !== '') {
				let tzoffset = (new Date(startDate)).getTimezoneOffset() * 60000;
				let localISOTime = (new Date(startDate - tzoffset)).toISOString().slice(0, -1);
				return localISOTime.split('T')[0];
			}
			else {
				return undefined;
			}
		}
		else if (label === ' To') {
			if (currentDate && currentDate !== '') {
				let tzoffset = (new Date(currentDate)).getTimezoneOffset() * 60000;
				let localISOTime = (new Date(currentDate - tzoffset)).toISOString().slice(0, -1);
				return localISOTime.split('T')[0];
			}
			else {
				return undefined;
			}
		}
		else {
			if (startDate && startDate !== '') {
				var tzoffset = (new Date(startDate)).getTimezoneOffset() * 60000;
				var localISOTime = (new Date(startDate - tzoffset)).toISOString().slice(0, -1);
				return localISOTime.split('T')[0];
			}
			else {
				return undefined;
			}
		}
	}

	async function submit() {
		if (submitLoading) {
			return;
		}

		setSubmitLoading(true);

		const formData = queryForm.current;
		let queryParameter;
		let filledReqFields = queries.find((query) => {
			queryParameter = {};
			const finalQuerySchema = query.every((e) => {
				if (e.hasOwnProperty("required") && e.hasOwnProperty("field")) {
					let paramValue = undefined;
					const formValue = formData[e.field]?.value;
					if (formValue) {
						switch (e.type) {
							case "int64":
							case "number":
								paramValue = Number(formValue);
								break;
							case "string":
								paramValue = formValue;
								break;
							case "array":
								paramValue = formValue.split(",");
								break;
							case "date-time":
								paramValue = convertStringtoIsoString(e.label);
								break;
							case "date":
								paramValue = convertStringtoDate(e.label);
								break;
							default:
								paramValue = formValue;
						}
					}

					queryParameter[e.field.split("|")[0]] = paramValue;
					if (e.required) {
						return paramValue ? true : false;
					}
					return true;
				}
				return false;
			});
			return finalQuerySchema;
		});

		if (!queryParameter || !filledReqFields) {
			addToastMessage.addToastMessage([t("fillInAllFields")], 'error')
			setSubmitLoading(false);
			return;
		}

		if (queryParameter.from) {
			queryParameter.snapshot_dt = queryParameter.snapshot_dt || {};
			queryParameter.snapshot_dt.from = queryParameter.from;
			delete queryParameter.from;
		}
		if (queryParameter.to) {
			queryParameter.snapshot_dt = queryParameter.snapshot_dt || {};
			queryParameter.snapshot_dt.to = queryParameter.to;
			delete queryParameter.to;
		}

		const payload = {
			participants: selectedParticipants,
			parameters: queryParameter,
			on_behalf_of: selectedOnBehalf ? [selectedOnBehalf] : [],
		};
		payload.participants = payload.participants.map(({ on_behalf_of, ...rest }) => rest)

		const [errorSubmit, data] = await handle(
			pull(selectedDataElement.id, payload)
		);

		setSubmitLoading(false);
		if (errorSubmit) {
			setError(errorSubmit?.message || "Unable to submit pull request");
			return;
		}
		goTo("/received-data/request/success");
	}

	function handleChange(field, event) {
		if (errors && errors.length > 0) {
			if (event) {
				let error = [...errors]
				const errorIndex = errors?.findIndex(
					(e) => e?.params?.missingProperty?.includes(field.split('|')[0]) || e?.params?.additionalProperty?.includes(field.split('|')[0]) || e.message?.includes(field.split('|')[0])
				);
				if (errorIndex > -1) {
					error.splice(errorIndex, 1);
					setErrors(error);
				}
			}
			else {
				checkRequiredFields()
			}
		}
	}

	function checkRequiredFields() {
		const check = queryForm.current
		let queryParameter
		queries.find(query => {
				queryParameter = {}
				query.forEach(e => {
					const formValue = check[e.field]?.value;
					const value =
						e.type === 'string' ? (formValue === '' ? undefined : formValue)
						: (e.type === 'number' || e.type === 'int64') ? (formValue === '' ? undefined : +formValue)
						: e.type === 'array' ? (formValue === '' ? undefined : formValue.split(','))
						: e.type === 'date-time' ? (formValue === '' ? undefined : convertStringtoIsoString(formValue))
						: (formValue === '' ? undefined : formValue)
					queryParameter[e.field.split('|')[0]] = value
				})

				if(Object.values(queryParameter).every(value => value === undefined)){
					return false
				}

				return true
		})
		if (queryParameter?.from) {
			queryParameter.snapshot_dt = queryParameter.snapshot_dt || {}
			queryParameter.snapshot_dt.from = queryParameter.from
			delete queryParameter.from
		}
		if (queryParameter?.to) {
			queryParameter.snapshot_dt = queryParameter.snapshot_dt || {}
			queryParameter.snapshot_dt.to = queryParameter.to
			delete queryParameter.to
		}
		const validate = ajv.compile(querySchema);
		const valid = validate({ ...queryParameter });
		if (!valid) {
			setErrors(validate.errors);
		} else {
			setErrors([]);
			submit()
		}
	}

	/*=================================================
		Custom Date Picker
	/================================================*/

	const CustomDatePicker = ({ field, dateFormat, fileType, label }) => {

		// const [initialDate, setInitialDate] = useState(new Date());

		const CustomTimeInput = ({ date, value, onChange }) => (
			<input
				value={value}
				onChange={(e) => onChange(e.target.value)}
			/>
		);

		return (

			<>
				{
					fileType === "date-time" && label === ' From' && (
						<DatePicker
							selected={startDate || ''}
							onChange={(date) => { setStartDate(date); handleChange(field, date) }}
							id={field}
							dateFormat={dateFormat}
							showTimeSelect
							dropdownMode="select"
							showMonthDropdown
							showYearDropdown
							placeholderText="Select date and time"
							customTimeInput={<CustomTimeInput />}
							calendarClassName="hide__time"
							isClearable={startDate ? true : false}
						/>
					)
				}
				{
					fileType === "date-time" && label === ' To' && (
						<DatePicker
							selected={currentDate}
							onChange={(date) => { setCurrentDate(date); handleChange(field, date) }}
							id={field}
							dateFormat={dateFormat}
							showTimeSelect
							dropdownMode="select"
							showMonthDropdown
							showYearDropdown
							placeholderText="Select date and time"
							customTimeInput={<CustomTimeInput />}
							calendarClassName="hide__time"
							isClearable={currentDate ? true : false}
							minDate={new Date(startDate)}
						/>
					)
				}
				{
					fileType === "date-time" && label !== ' To' && label !== ' From' && (
						<DatePicker
							selected={currentDate}
							onChange={(date) => { setCurrentDate(date); handleChange(field, date) }}
							id={field}
							dateFormat={dateFormat}
							showTimeSelect
							dropdownMode="select"
							showMonthDropdown
							showYearDropdown
							placeholderText="Select date and time"
							customTimeInput={<CustomTimeInput />}
							calendarClassName="hide__time"
							isClearable={currentDate ? true : false}
						/>
					)
				}
				{
					fileType !== "date-time" && label === ' From' && (
						<DatePicker
							selected={startDate}
							onChange={(date) => { setStartDate(date); handleChange(field, date) }}
							id={field}
							dropdownMode="select"
							showMonthDropdown
							showYearDropdown
							dateFormat={dateFormat}
							placeholderText="Select date"
							isClearable={startDate ? true : false}
						/>
					)
				}
				{
					fileType !== "date-time" && label === ' To' && (
						<DatePicker
							selected={currentDate}
							onChange={(date) => { setCurrentDate(date); handleChange(field, date) }}
							id={field}
							dropdownMode="select"
							showMonthDropdown
							showYearDropdown
							dateFormat={dateFormat}
							placeholderText="Select date"
							isClearable={currentDate ? true : false}
							minDate={new Date(startDate)}
						/>
					)
				}
				{
					fileType !== "date-time" && label !== ' To' && label !== ' From' && (
						<DatePicker
							selected={startDate}
							onChange={(date) => { setStartDate(date); handleChange(field, date) }}
							id={field}
							dropdownMode="select"
							showMonthDropdown
							showYearDropdown
							dateFormat={dateFormat}
							placeholderText="Select date"
							isClearable={startDate ? true : false}
						/>
					)
				}
			</>
		)
	}

	function getErrorMessageForField (field){
		const fieldName = field.split('|')[0];
		const formattedFieldName = '/' + fieldName;

		const mandatoryField = errors?.find((e) =>
			e?.params?.missingProperty?.includes(fieldName) ||
			e?.params?.additionalProperty?.includes(fieldName) ||
			e.message?.includes(field)
		)

		const typeError = errors?.find((e) =>
			e?.instancePath.includes(formattedFieldName) && e?.keyword === 'type'
		)

		if(mandatoryField){
			return "This is a mandatory field."
		}

		if(typeError){
			return `Invalid data type. It ${typeError.message}`
		}
	}

	return (
		<>
			<div className="title-header">
				<div className="title-header__left">
					<div className="title-header__title">
						{pageTitle}
					</div>
				</div>

				<div className="title-header__right">

				</div>
			</div>

			<Stepper  steps={steps} currentStep={step-1}/>
			<SelectedListDetails />

			<hr />

			{step === 1 && (
				<>
					<span className="t--share-data-subheader">{t("chooseDataElementToRetrieve")}</span>
					<div className="data-element-selectors" style={dataElementSelectorStyles}>
						<div className="data-element-selectors__cols">
							<ElementsList />
						</div>
					</div>

					{/* <div className="data-elements-actions" style={{ marginTop: rem(35) }}>
						<div className="data-elements-actions__cols">
							<div className="data-elements-actions__col">
								<Button sizing='Medium' text='Next' full round maxWidth='154' submit={() => goToStep(2)} disabled={!stepOneStatus} />
							</div>
						</div>
					</div> */}
				</>
			)}

			{step === 2 && (
				<>
					<span className="t--share-data-subheader">{t("chooseWhomToRetrieved")}</span>
					<div className="data-element-selectors" style={dataElementSelectorStyles}>
						<div className="data-element-selectors__cols">
							<ParticipantsList />
						</div>
					</div>

					{/* <div className="data-elements-actions" style={{ marginTop: rem(35) }}>
						<div className="data-elements-actions__cols">
							<div className="data-elements-actions__col">
								<Button sizing='Medium' text='Back' ghost full round maxWidth='154' submit={() => goToStep(1)} />
							</div>
							<div className="data-elements-actions__col">
								<Button sizing='Medium' text='Next' full round maxWidth='154' submit={() => goToStep(3)} disabled={!stepTwoStatus} />
							</div>
						</div>
					</div> */}
				</>
			)}

			{step === 3 && (
				<>
					<div className="additional_information_request">Please provide the following pull parameters for the specific data element</div>
					<div className="request">
						<div className="request__query">
							<div className="request__query__items">
								<form ref={queryForm}>
									{querySchema && (
										queries.map((qrys, index) => {
											return (<Fragment key={index}>
												{index > 0 && (<div className="request__or">or</div>)}
												{qrys.map((query, index) => {
													const { label, description, type, field, required } = query
													const label_required = `${label}${required ? ' *' : ''}`
													const error = getErrorMessageForField(field)

													return (
														<div className="request__query__item" key={index}>
															{type === 'string' && (
																<Input
																	label={label_required}
																	id={field}
																	onKeyDown={event => { onKeyDown(event) }}
																	tabIndex="0"
																	name={field}
																	onChange={(e) => handleChange(field, e.target.value)}
																	error={error}
																	errorMessage={error}
																/>
															)}
															{type === 'array' && (
																<Input
																	label={label_required}
																	id={field}
																	onKeyDown={event => { onKeyDown(event) }}
																	tabIndex="0"
																	name={field}
																	onChange={(e) => handleChange(field, e.target.value)}
																	error={error}
																	errorMessage={error}
																/>
															)}
															{type === 'date' && (
																<>
																	<div className='input-field__label'>{label_required}</div>
																	<CustomDatePicker
																		field={field}
																		dateFormat="yyyy-MM-dd"
																		fileType={type}
																		label={label}
																	/>
																	{error && <span className='t--required'>{error}</span>}
																</>
															)}
															{type === 'date-time' && (
																<>
																	<div className='input-field__label'>{label_required}</div>
																	<CustomDatePicker
																		field={field}
																		dateFormat="yyyy-MM-dd HH:mm:ss SSS"
																		fileType={type}
																		label={label}
																	/>
																	{error && <span className='t--required'>{error}</span>}
																</>
															)}
															{(type === 'number' || type === 'int64') && (
																<Input
																	label={label_required}
																	id={field}
																	type="number"
																	name={field}
																	onChange={(e) => handleChange(field, e.target.value)}
																	error={error}
																	errorMessage={error}
																/>
															)}
														</div>
													)
												})}
												{/* <hr /> */}
											</Fragment>)
										})
									)}
								</form>
							</div>
						</div>
						<div className="request__onbehalf">
							<div className="additional-zone__side">
								<AdditionalZone on_behalf_of={onBehalfOf} flow="request" callback={params => onSelectedOnBehalf(params)} />
							</div>
						</div>
					</div>

					{/* <div className="data-elements-actions" style={{ marginTop: rem(35) }}>
						<div className="data-elements-actions__cols">
							<div className="data-elements-actions__col">
								<Button sizing='Medium' text='Back' ghost full round maxWidth='154' submit={() => goToStep(2)} />
							</div>
							<div className="data-elements-actions__col">
								<Button sizing='Medium' text='Submit' full round maxWidth='154' disabled={!(stepThreeStatus && stepThreeBStatus)} submit={submit} />
							</div>
						</div>
					</div> */}
				</>
			)}

			<div style={{ height: rem(72) }}></div>
			<div className="DS__layout__footer">
				<div className="layout-default__sticky-actions">
					<KeyboardEventHandler handleKeys={["alt+right", "alt+left", "enter", "esc"]} onKeyEvent={(key, e) => {
						if (key === "alt+left" || key === "esc") {
							prevStep()
						}
						if (key === "alt+right" || key === "enter") {
							if (stepOneStatus && step === 1) {
								nextStep()
							}
							if (stepTwoStatus && step === 2) {
								nextStep()
							}
							if ((stepThreeBStatus) && step === steps.length) {
								submit()
							}
						}
					}} />
					<div className="layout-default__sticky-actions__left">
						{step > 1 &&
							<Button text={t("back")}
								sizing='Medium' ghost full minWidth="96" maxWidth='154'
								submit={() => prevStep()}
							/>
						}
					</div>
					<div className="layout-default__sticky-actions__right">
						{step === 1 &&
							<Button text={t("next")}
								sizing='Medium' full minWidth="96" maxWidth='154'
								disabled={!stepOneStatus}
								submit={() => nextStep()}
							/>
						}
						{step === 2 &&
							<Button text={t("next")}
								sizing='Medium' full minWidth="96"  maxWidth='154'
								disabled={!stepTwoStatus}
								submit={() => nextStep()}
							/>
						}
						{step === 3 &&
							<Button text={t("submit")}
								id="received-data-submit-button-id"
								sizing='Medium' full minWidth="96"  maxWidth='154'
								disabled={!(stepThreeBStatus)}
								submit={checkRequiredFields}
							/>
						}
					</div>
				</div>
			</div>
			{
				submitLoading && (
					ReactDOM.createPortal(
						<div className='modal white'>
							<div className='loading'>
								<img src="/loading.gif" />
							</div>
						</div>
						, document.body
					)
				)
			}

			<Toast2 show={error} errorMessage={error} close={() => { handleError() }} />
		</>
	)
}

export default Page
